/**
 * Starting carton size and weight for the Create UPS Shipment dialog.
 * A few packed units use the small carton. Fuller cartons use the standard carton.
 */

export const UPS_STANDARD_CARTON = {
  length: "22",
  width: "22",
  height: "12",
  weight: "35",
} as const;

export const UPS_SMALL_CARTON = {
  length: "10",
  width: "10",
  height: "10",
  weight: "5",
} as const;

/** Packed unit count at or below this uses the small carton. */
export const UPS_SMALL_CARTON_MAX_UNITS = 4;

export type UpsCartonDraft = {
  length: string;
  width: string;
  height: string;
  weight: string;
};

export function upsCartonDraftForUnitCount(unitCount: number | null | undefined): UpsCartonDraft {
  if (
    unitCount != null &&
    Number.isFinite(unitCount) &&
    unitCount > 0 &&
    unitCount <= UPS_SMALL_CARTON_MAX_UNITS
  ) {
    return { ...UPS_SMALL_CARTON };
  }
  return { ...UPS_STANDARD_CARTON };
}

export function cartonUnitCountsFromSkuQuantities(
  rows: Array<{ boxed?: number | null; carton_key?: number | null }> | null | undefined,
): number[] {
  if (rows == null || rows.length === 0) return [];
  const byCarton = new Map<number, number>();
  for (const row of rows) {
    const cartonKey = Number(row.carton_key);
    if (!Number.isFinite(cartonKey) || cartonKey <= 0) continue;
    const boxed = Number(row.boxed ?? 0);
    if (!Number.isFinite(boxed) || boxed <= 0) continue;
    byCarton.set(cartonKey, (byCarton.get(cartonKey) ?? 0) + boxed);
  }
  return [...byCarton.entries()]
    .sort((left, right) => left[0] - right[0])
    .map((entry) => entry[1]);
}

export function buildUpsPackageDrafts(
  cartonCount: number,
  cartonUnitCounts: number[],
): UpsCartonDraft[] {
  if (cartonUnitCounts.length > 0) {
    return cartonUnitCounts.map((unitCount) => upsCartonDraftForUnitCount(unitCount));
  }
  const packageCount = Math.max(1, Math.trunc(Number.isFinite(cartonCount) ? cartonCount : 0));
  return Array.from({ length: packageCount }, () => upsCartonDraftForUnitCount(null));
}
